import { HEADER_BYTES, OK, assertField, checkHeader, putHeader, putU16, readU16, type DecodeVerdict } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { MAX_U16 } from "./limits.ts";

/**
 * The `respawn` frame (ADR-073's amendment "the respawn is the player's", PROTOCOL.md 5.15): 5 bytes, client to
 * server, the dead player's request to stand up at a point of its last `respawnOffer`, by the point's index there. The
 * decoder never throws (ADR-030).
 */

/**
 * Total frame length.
 */
export const RESPAWN_BYTES = 5;

/**
 * The most points a `respawnOffer` carries, so a request's index is checked against at most this many.
 */
export const MAX_RESPAWN_POINTS = 16;

/**
 * The longest name a respawn point carries on the wire, in UTF-16 units.
 */
export const MAX_RESPAWN_POINT_NAME_CHARS = 48;

/**
 * A respawn point's kind on the wire: lower-case letters, so a kind a later server names is a value a page words as it
 * words the spawn rather than a malformed frame.
 */
export const RESPAWN_POINT_KIND_PATTERN = /^[a-z]+$/;

/**
 * The longest respawn point kind on the wire.
 */
export const MAX_RESPAWN_POINT_KIND_CHARS = 16;

/**
 * The kinds of respawn point this build offers: a shrine (ADR-073 decision 12) or the map's spawn, where the map has no
 * shrine a character can stand at.
 */
export const RespawnPointKind = {
  Shrine: "shrine",
  Spawn: "spawn",
} as const;

/**
 * A decoded or to-be-encoded `respawn`, reused across frames.
 */
export interface RespawnRecord {
  point: number;
}

/**
 * A fresh record for `decodeRespawn` to fill.
 *
 * @returns A zeroed record.
 */
export function newRespawnRecord(): RespawnRecord {
  return { point: 0 };
}

/**
 * Encodes a `respawn` into a caller-owned buffer.
 *
 * @param buffer Destination, at least `RESPAWN_BYTES` long.
 * @param respawn The point's index in the last offer.
 * @returns Bytes written, always `RESPAWN_BYTES`.
 * @throws Error when the buffer is too small or the point is not a `u16`.
 */
export function encodeRespawn(buffer: Uint8Array, respawn: Readonly<RespawnRecord>): number {
  if (buffer.length < RESPAWN_BYTES) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a ${RESPAWN_BYTES}-byte respawn`);
  }
  assertField(respawn.point, MAX_U16, `respawn point ${respawn.point}`);
  putHeader(buffer, MessageType.Respawn);
  putU16(buffer, HEADER_BYTES, respawn.point);
  return RESPAWN_BYTES;
}

/**
 * Decodes a `respawn` frame into a record. Never throws: a wrong length or type is malformed and a wrong version a
 * mismatch; every point is well formed, and whether it names a point offered is the map server's judgement.
 *
 * @param frame The whole frame.
 * @param out Record to fill; untouched unless the verdict is `ok`.
 * @returns The verdict.
 */
export function decodeRespawn(frame: Uint8Array, out: RespawnRecord): DecodeVerdict {
  const header = checkHeader(frame, MessageType.Respawn, RESPAWN_BYTES);
  if (header.kind !== "ok") {
    return header;
  }
  out.point = readU16(frame, HEADER_BYTES);
  return OK;
}
